import { useState } from 'react';
import { ChevronRight, Grid3X3, Users } from 'lucide-react';
import type { VendorFloorPlan } from '../../api/portal';
import { useVendorEventFloorPlans } from '../../hooks/usePortal';
import { ClientFloorPlanCanvas } from '../floor-plan/ClientFloorPlanCanvas';
import { ClientPlanPart } from './ClientPlanPart';

/** Who sits at each table, named by the table's number and (when shared) its label. */
function PlanSeating({ plan }: { plan: VendorFloorPlan }) {
  const tables = plan.seating
    .map((row) => {
      const table = plan.objects.find(
        (o) => (o.config as { uid?: unknown } | null)?.uid === row.tableUid,
      );
      return { ...row, tableNumber: table?.tableNumber ?? null, label: table?.label ?? null };
    })
    .sort((a, b) => (a.tableNumber ?? Infinity) - (b.tableNumber ?? Infinity));
  if (tables.length === 0) {
    return <p className="text-sm text-gray-500">Nobody is seated on this plan yet.</p>;
  }
  return (
    <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200" aria-label="Seating">
      {tables.map((table) => (
        <li key={table.tableUid} className="px-3 py-2 text-sm">
          <span className="font-medium text-gray-900">
            {table.tableNumber != null ? `Table ${table.tableNumber}` : 'Table'}
            {table.label ? ` · ${table.label}` : ''}
          </span>
          <span className="block text-gray-600">{table.guests.join(', ')}</span>
        </li>
      ))}
    </ul>
  );
}

export interface VendorFloorPlansPanelProps {
  eventId: string;
  eventName?: string;
}

/**
 * The floor plans the office shared with a vendor, read only — drawn as a
 * client sees them, and with the seating listed only when the office shared
 * guest names too (the server sends none otherwise).
 */
export function VendorFloorPlansPanel({ eventId, eventName }: VendorFloorPlansPanelProps) {
  const { data: plans = [], isLoading, isError } = useVendorEventFloorPlans(eventId);
  const [openPlan, setOpenPlan] = useState<string | null>(null);

  return (
    <div data-testid="vendor-floor-plans" className="space-y-3">
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <Grid3X3 size={18} />
        Floor plans
      </h3>
      {isLoading && <p className="text-sm text-gray-500">Loading floor plans...</p>}
      {isError && (
        <p role="alert" className="text-sm text-red-600">
          The floor plans could not be loaded.
        </p>
      )}
      {!isLoading && !isError && plans.length === 0 && (
        <p className="text-sm text-gray-500">No floor plans shared.</p>
      )}
      {plans.map((plan) => {
        const isOpen = openPlan === plan.id || plans.length === 1;
        return (
          <div key={plan.id} className="rounded-lg border bg-white overflow-hidden">
            <button
              type="button"
              aria-expanded={isOpen}
              className="flex min-h-11 w-full items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() => setOpenPlan(openPlan === plan.id ? null : plan.id)}
            >
              <div>
                <h4 className="font-semibold">{plan.name}</h4>
                <ClientPlanPart plan={plan} />
                {plan.roomName && <p className="text-sm text-gray-500">{plan.roomName}</p>}
              </div>
              <ChevronRight
                size={18}
                className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="space-y-4 border-t p-4">
                <ClientFloorPlanCanvas plan={plan} eventName={eventName} />
                {plan.guestNames && (
                  <div className="space-y-2">
                    <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                      <Users size={14} /> Who sits where
                    </p>
                    <PlanSeating plan={plan} />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
